import { HELP_PAGES, helpPage, type HelpPage } from "@joogoos/contracts";

/**
 * Help (docs/19, ADR 0026): one page per tab, Studio step and account panel, written about Joogoos as it is today.
 *
 * A panel of the account dialog rather than a page of its own, because this app has no addresses to put one at, and
 * because it is reached the way Your reports is: from the dialog's own list, or from Help, which opens the page it
 * answered from and the page for the screen it was opened over. The pages are data (`help/pages.ts`), so what a page says and what it links to are
 * tested, and so the same sentences are what a help assistant will one day be allowed to repeat.
 */
export function Help({ selected, onSelect, onAsk }: {
  selected: string | null;
  onSelect: (slug: string | null) => void;
  /** Opens Help's box — asking, and the report form in it — over this panel. Absent where nothing can open it. */
  onAsk?: (() => void) | undefined;
}) {
  const page = selected ? helpPage(selected) : null;
  return page ? <Page page={page} onSelect={onSelect} /> : <Index onSelect={onSelect} onAsk={onAsk} />;
}

function Index({ onSelect, onAsk }: { onSelect: (slug: string) => void; onAsk?: (() => void) | undefined }) {
  return (
    <div className="flex flex-col gap-3 max-w-[620px]">
      <p className="text-[12.5px] text-muted leading-relaxed">
        How each screen works, written about Joogoos as it is today. Where a screen does not do something yet, its page
        says so.
      </p>
      {/* The way to a person, from the place somebody arrives at when a page did not answer them (ADR 0024 §2). */}
      {onAsk ? (
        <button
          onClick={onAsk}
          className="self-start px-3.5 py-2 rounded-lg bg-ink text-white text-[13px] font-medium"
        >
          Ask a question, or report a problem
        </button>
      ) : null}
      <ul className="bg-panel border border-line rounded-[10px] divide-y divide-line">
        {HELP_PAGES.map((p) => (
          <li key={p.slug}>
            <button onClick={() => onSelect(p.slug)} className="w-full text-left px-3.5 py-2.5 hover:bg-page">
              <span className="block text-[13.5px] font-medium">{p.title}</span>
              <span className="block text-[11.5px] text-muted leading-snug">{p.summary}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Page({ page, onSelect }: { page: HelpPage; onSelect: (slug: string | null) => void }) {
  const related = page.related.map(helpPage).filter((p): p is HelpPage => p !== null);
  return (
    <article className="flex flex-col gap-4 max-w-[620px]">
      <button onClick={() => onSelect(null)} className="self-start text-xs text-muted hover:text-ink">← All help</button>
      <header>
        <h3 className="text-[17px] font-semibold">{page.title}</h3>
        <p className="text-[13px] text-muted mt-0.5">{page.summary}</p>
      </header>

      {page.sections.map((s) => (
        <section key={s.heading}>
          <h4 className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted mb-1.5">{s.heading}</h4>
          {s.body.map((b) => <p key={b} className="text-[13px] text-ink-2 leading-relaxed mb-1.5">{b}</p>)}
          {s.list?.length ? (
            <ul className="list-disc pl-5 flex flex-col gap-1 text-[13px] text-ink-2 leading-relaxed">
              {s.list.map((item) => <li key={item}>{item}</li>)}
            </ul>
          ) : null}
        </section>
      ))}

      {/* Said plainly, so nobody goes looking for what is not there. */}
      {page.notYet?.length ? (
        <section className="bg-tint rounded-[10px] px-3.5 py-2.5" aria-label="Not yet">
          <h4 className="text-[11px] font-semibold uppercase tracking-[.06em] text-coral-2 mb-1.5">Not yet</h4>
          <ul className="list-disc pl-5 flex flex-col gap-1 text-[13px] text-ink-2 leading-relaxed">
            {page.notYet.map((item) => <li key={item}>{item}</li>)}
          </ul>
        </section>
      ) : null}

      {related.length ? (
        <nav aria-label="See also">
          <h4 className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted mb-1.5">See also</h4>
          <div className="flex flex-wrap gap-1.5">
            {related.map((r) => (
              <button key={r.slug} onClick={() => onSelect(r.slug)} className="px-2.5 py-1 rounded-lg border border-line-2 bg-panel text-xs font-medium text-ink-2 hover:text-ink">
                {r.title}
              </button>
            ))}
          </div>
        </nav>
      ) : null}
    </article>
  );
}
